export type ReportCategoryCalculationInput<K extends string = string> = {
  key: K;
  canTransferLessons: boolean;
  /**
   * Kategoriloftet: hvor mange lektioner af DENNE type der må tælle med. Udelades,
   * når kategorien ikke har noget loft — kursusdeltagelse har ingen grænse i reglerne
   * (jf. `// ingen max på kursus` i den gamle formular på main). Hvor meget der i alt
   * må indberettes, styres af `requiredLessons`, ikke af kategoriloftet.
   */
  maxReported?: number;
  /** Kategoriloftet for, hvad der må overføres til næste periode. */
  maxTransfer: number;
  selectedLessons: number;
  transferredFromPreviousLessons?: number;
};

export type ReportCategoryCalculationSummary<K extends string = string> = {
  key: K;
  selectedLessons: number;
  transferredFromPreviousLessons: number;
  totalLessons: number;
  submittedLessons: number;
  transferredToNext: number;
  /** Lektioner over kategoriloftet, som hverken indberettes eller overføres. */
  droppedLessons: number;
};

export type ReportCategoryCalculationOptions = {
  /**
   * Spærrer for overførsel, indtil `requiredLessons` er indberettet.
   *
   * BEGGE kaldere sætter den til true, og ingen sætter den til false — default'en herunder
   * findes kun for at gøre feltet valgfrit. Årsagerne er forskellige:
   *
   * - Efteruddannelsesperioder: backenden afviser ENHVER overførsel under `requiredLessons`
   *   (EfteruddannelseIndberetningDraftHelper.ValidateLessonSplit). Uden spærren kunne UI'et
   *   bygge en payload, backenden svarer 400 på — fx 40 lektioner undervisning og intet
   *   andet: indberettet 27, overført 13, hårdt 400 ved sidste klik.
   * - Påbud/pålæg: her er backenden mere tolerant (PaabudIndberetningDraftHelper.ValidateLessons
   *   validerer kun sum og advokatvagt), men reglen er den samme, og UI'et er stedet den
   *   håndhæves. Se calculateInjunctionCategorySummaries og kommentaren ved
   *   EfteruddannelseIndberetningDraftHelper.ValidateLessons.
   *
   * Sætter man den til false, flytter man altså en regel ud af det eneste lag, der
   * håndhæver den for afgørelser.
   */
  requireFullReportBeforeTransfer?: boolean;
  /** Samlet loft for overførsel på tværs af kategorier. */
  maxTotalTransfer?: number;
};

export const MAX_TOTAL_TRANSFER_LESSONS = 54;

// To forskellige lofter, som ikke må blandes sammen:
//   `maxReported`  — hvor meget af DENNE kategori der må tælle med (27 undervisning,
//                    18 forfatter, …). Udeladt = ingen grænse, fx kursusdeltagelse.
//   `maxTransfer`  — hvor meget af kategorien der må overføres til næste periode.
//                    På tværs af kategorier gælder desuden `maxTotalTransfer`.
// Overskud ud over lektionskravet drænes til `transferredToNext` i input-rækkefølge
// (kun kategorier, der må overføres) — kaldere skal derfor liste kategorierne i
// dræn-rækkefølge: kursus, undervisning, forfatter, og så resten.
// Det, der stadig ligger over kravet bagefter, bliver indberettet — der beskæres
// ikke ned til lektionskravet. Overskuddet er alligevel uden værdi (overførsel er
// loftet), men det registreres frem for at forsvinde. Samme regel som den gamle
// formular på main.
export const calculateReportCategorySummaries = <K extends string>(
  categories: ReportCategoryCalculationInput<K>[],
  requiredLessons: number,
  options: ReportCategoryCalculationOptions = {},
): ReportCategoryCalculationSummary<K>[] => {
  const {
    requireFullReportBeforeTransfer = false,
    maxTotalTransfer = MAX_TOTAL_TRANSFER_LESSONS,
  } = options;

  // Trin 1: opgør hver kategori uden endnu at fordele overskuddet.
  const summaries = categories.map((category) => {
    const transferredFromPreviousLessons =
      category.transferredFromPreviousLessons ?? 0;
    const totalLessons =
      transferredFromPreviousLessons + category.selectedLessons;
    const submittedLessons = Math.min(
      totalLessons,
      category.maxReported ?? Infinity,
    );

    return {
      key: category.key,
      selectedLessons: category.selectedLessons,
      transferredFromPreviousLessons,
      totalLessons,
      submittedLessons,
      transferredToNext: 0,
      droppedLessons: totalLessons - submittedLessons,
    };
  });

  // Trin 2: spærren. Er lektionskravet ikke nået, må intet overføres — alt over
  // kategoriloftet falder bort i stedet.
  const meetsRequirement =
    getTotalSubmittedLessons(summaries) >= requiredLessons;
  if (requireFullReportBeforeTransfer && !meetsRequirement) {
    return summaries;
  }

  // Trin 3a: overskud ud over kategoriloftet overføres.
  let remainingTransferCapacity = maxTotalTransfer;
  for (const [index, category] of categories.entries()) {
    const summary = summaries[index];
    const overflow = summary.droppedLessons;
    const transferredToNext = category.canTransferLessons
      ? Math.min(overflow, category.maxTransfer, remainingTransferCapacity)
      : 0;

    remainingTransferCapacity -= transferredToNext;
    summary.transferredToNext = transferredToNext;
    summary.droppedLessons = overflow - transferredToNext;
  }

  // Trin 3b: overskud ud over lektionskravet drænes i input-rækkefølge.
  let remainingExcess = Math.max(
    getTotalSubmittedLessons(summaries) - requiredLessons,
    0,
  );

  for (const [index, category] of categories.entries()) {
    if (remainingExcess <= 0 || remainingTransferCapacity <= 0) {
      break;
    }

    if (!category.canTransferLessons) {
      continue;
    }

    const summary = summaries[index];
    const categoryCapacity = category.maxTransfer - summary.transferredToNext;
    const drainAmount = Math.min(
      remainingExcess,
      summary.submittedLessons,
      categoryCapacity,
      remainingTransferCapacity,
    );

    summary.submittedLessons -= drainAmount;
    summary.transferredToNext += drainAmount;
    remainingExcess -= drainAmount;
    remainingTransferCapacity -= drainAmount;
  }

  return summaries;
};

export const getTotalSubmittedLessons = (
  summaries: { submittedLessons: number }[],
): number =>
  summaries.reduce((total, summary) => total + summary.submittedLessons, 0);

/**
 * Lektioner, der bliver til noget: indberettet nu eller overført til næste periode.
 * Bruges til overskriften — det valgte minus det, der bortfalder.
 */
export const getTotalUsableLessons = (
  summaries: { submittedLessons: number; transferredToNext: number }[],
): number =>
  summaries.reduce(
    (total, summary) =>
      total + summary.submittedLessons + summary.transferredToNext,
    0,
  );

/** Alt, der bortfalder, fordi det ligger over kategoriloftet og ikke kan overføres. */
export const getTotalDroppedLessons = (
  summaries: { droppedLessons: number }[],
): number =>
  summaries.reduce((total, summary) => total + summary.droppedLessons, 0);
